import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CategoryInterface } from "@core/interfaces/forums/category.interface";
import {
  CommunityNoteHistoryInterface,
  CommunityNoteInterface,
  CommunityNoteVoteInterface,
  CreateCommunityNoteDto,
  UpdateCommunityNoteDto
} from "@core/interfaces/forums/community-note.interface";
import { ForumInterface } from "@core/interfaces/forums/forum.interface";
import { PollType } from "@core/interfaces/forums/poll-type.enum";
import { PostBadgesResponse } from "@core/interfaces/forums/post-badge.interface";
import { PostInterface } from "@core/interfaces/forums/post.interface";
import { TagRecommendationsInterface } from "@core/interfaces/forums/tag-recommendation.interface";
import { TagInterface } from "@core/interfaces/forums/tag.interface";
import {
  TopicTagGenerationRequestInterface,
  TopicTagGenerationResponseInterface
} from "@core/interfaces/forums/topic-tag-generation.interface";
import {
  TopicTitleGenerationRequestInterface,
  TopicTitleGenerationResponseInterface
} from "@core/interfaces/forums/topic-title-generation.interface";
import {
  TopicTagInterface,
  SimilarTopicInterface,
  SimilarTopicSearchRequestInterface,
  TopicInterface,
  TagReviewRequestInterface
} from "@core/interfaces/forums/topic.interface";
import { PaginatedApiResultInterface } from "@core/services/api/interfaces/paginated-api-result.interface";
import { LoadingService } from "@core/services/loading.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { ForumConstants } from "@features/forum/constants";
import { TagSelectionService } from "@features/forum/services/tag-selection.service";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { BaseClassicApiService } from "../classic/base-classic-api.service";

@Injectable({
  providedIn: "root"
})
export class ForumApiService extends BaseClassicApiService {
  configUrl = this.baseUrl + "/forum";

  /**
   * Helper method to add common search filter parameters to a URL
   */
  private addSearchFilterParams(
    url: string,
    params: {
      name?: string;
      titleSearch?: string;
      dateFrom?: string;
      dateTo?: string;
      myTopics?: boolean;
      myPosts?: boolean;
    }
  ): string {
    if (params.name) {
      url = UtilsService.addOrUpdateUrlParam(url, "q", params.name);
    }

    if (params.titleSearch) {
      url = UtilsService.addOrUpdateUrlParam(url, "title-search", params.titleSearch);
    }

    if (params.dateFrom) {
      url = UtilsService.addOrUpdateUrlParam(url, "date-from", params.dateFrom);
    }

    if (params.dateTo) {
      url = UtilsService.addOrUpdateUrlParam(url, "date-to", params.dateTo);
    }

    if (params.myTopics) {
      url = UtilsService.addOrUpdateUrlParam(url, "my-topics", "true");
    }

    if (params.myPosts) {
      url = UtilsService.addOrUpdateUrlParam(url, "my-posts", "true");
    }

    return url;
  }

  constructor(
    public readonly loadingService: LoadingService,
    public readonly http: HttpClient,
    private readonly tagSelectionService: TagSelectionService,
    private readonly translateService: TranslateService
  ) {
    super(loadingService);
  }

  getCategory(categoryId: CategoryInterface["id"]): Observable<CategoryInterface> {
    return this.http.get<CategoryInterface>(`${this.configUrl}/category/${categoryId}/`);
  }

  getCategories(): Observable<PaginatedApiResultInterface<CategoryInterface>> {
    return this.http.get<PaginatedApiResultInterface<CategoryInterface>>(`${this.configUrl}/category/`);
  }

  getForum(forumId: ForumInterface["id"] | null = null, slug: string | null = null): Observable<ForumInterface> {
    if (slug) {
      let url = `${this.configUrl}/forum/`;
      url = UtilsService.addOrUpdateUrlParam(url, "slug", slug);
      return this.http.get<ForumInterface>(url);
    }

    return this.http.get<ForumInterface>(`${this.configUrl}/forum/${forumId}/`);
  }

  loadForums(
    categoryId?: CategoryInterface["id"],
    parentForumId?: ForumInterface["id"],
    page = 1
  ): Observable<PaginatedApiResultInterface<ForumInterface>> {
    let url = this.configUrl + "/forum/";

    if (categoryId) {
      url = UtilsService.addOrUpdateUrlParam(url, "category", categoryId.toString());
    }

    if (parentForumId) {
      url = UtilsService.addOrUpdateUrlParam(url, "parent", parentForumId.toString());
    }

    url = UtilsService.addOrUpdateUrlParam(url, "page", page.toString());

    return this.http.get<PaginatedApiResultInterface<ForumInterface>>(url);
  }

  getTopic(topicId: TopicInterface["id"] | null = null, slug: string | null = null): Observable<TopicInterface> {
    const request$ = slug
      ? this.http.get<TopicInterface>(UtilsService.addOrUpdateUrlParam(`${this.configUrl}/topic/`, "slug", slug))
      : this.http.get<TopicInterface>(`${this.configUrl}/topic/${topicId}/`);

    return request$.pipe(
      map(topic => ({
        ...topic,
        primaryTag: topic.tags ? this.tagSelectionService.selectPrimaryTag(topic.tags) : null
      }))
    );
  }

  loadTopics(
    forumId?: ForumInterface["id"],
    page = 1,
    subscribed?: boolean,
    name?: string,
    recentParticipants = true,
    titleSearch?: string,
    dateFrom?: string,
    dateTo?: string,
    myTopics?: boolean,
    myPosts?: boolean
  ): Observable<PaginatedApiResultInterface<TopicInterface>> {
    let url = this.configUrl + "/topic/";

    if (forumId) {
      url = UtilsService.addOrUpdateUrlParam(url, "forum", forumId.toString());
    }

    url = UtilsService.addOrUpdateUrlParam(url, "page", page.toString());

    if (subscribed) {
      url = UtilsService.addOrUpdateUrlParam(url, "subscribed", "true");
    }

    // Use helper method for search filter params
    url = this.addSearchFilterParams(url, {
      name,
      titleSearch,
      dateFrom,
      dateTo,
      myTopics,
      myPosts
    });

    if (recentParticipants) {
      url = UtilsService.addOrUpdateUrlParam(url, "recent_participants", "1");
    }

    return this.http.get<PaginatedApiResultInterface<TopicInterface>>(url).pipe(
      map(response => ({
        ...response,
        results: response.results.map(topic => ({
          ...topic,
          primaryTag: topic.tags ? this.tagSelectionService.selectPrimaryTag(topic.tags) : null
        }))
      }))
    );
  }

  latestTopics(
    page = 1,
    subscribed?: boolean,
    name?: string,
    recentParticipants = true,
    titleSearch?: string,
    dateFrom?: string,
    dateTo?: string,
    myTopics?: boolean,
    myPosts?: boolean
  ): Observable<PaginatedApiResultInterface<TopicInterface>> {
    let url = `${this.configUrl}/topic/latest/?page=${page}`;

    if (subscribed) {
      url = UtilsService.addOrUpdateUrlParam(url, "subscribed", "true");
    }

    // Use helper method for search filter params
    url = this.addSearchFilterParams(url, {
      name,
      titleSearch,
      dateFrom,
      dateTo,
      myTopics,
      myPosts
    });

    if (recentParticipants) {
      url = UtilsService.addOrUpdateUrlParam(url, "recent_participants", "1");
    }

    return this.http.get<PaginatedApiResultInterface<TopicInterface>>(url).pipe(
      map(response => ({
        ...response,
        results: response.results.map(topic => ({
          ...topic,
          primaryTag: topic.tags ? this.tagSelectionService.selectPrimaryTag(topic.tags) : null
        }))
      }))
    );
  }

  activeTopics(
    page = 1,
    subscribed?: boolean,
    name?: string,
    recentParticipants = true,
    titleSearch?: string,
    dateFrom?: string,
    dateTo?: string,
    myTopics?: boolean,
    myPosts?: boolean
  ): Observable<PaginatedApiResultInterface<TopicInterface>> {
    let url = `${this.configUrl}/topic/active/?page=${page}`;

    if (subscribed) {
      url = UtilsService.addOrUpdateUrlParam(url, "subscribed", "true");
    }

    // Use helper method for search filter params
    url = this.addSearchFilterParams(url, {
      name,
      titleSearch,
      dateFrom,
      dateTo,
      myTopics,
      myPosts
    });

    if (recentParticipants) {
      url = UtilsService.addOrUpdateUrlParam(url, "recent_participants", "1");
    }

    return this.http.get<PaginatedApiResultInterface<TopicInterface>>(url).pipe(
      map(response => ({
        ...response,
        results: response.results.map(topic => ({
          ...topic,
          primaryTag: topic.tags ? this.tagSelectionService.selectPrimaryTag(topic.tags) : null
        }))
      }))
    );
  }

  getPost(postId: PostInterface["id"]): Observable<PostInterface> {
    return this.http.get<PostInterface>(`${this.configUrl}/post/${postId}/`);
  }

  loadPosts(topicId: TopicInterface["id"], page = 1): Observable<PaginatedApiResultInterface<PostInterface>> {
    let url = this.configUrl + "/post/";

    url = UtilsService.addOrUpdateUrlParam(url, "topic", topicId.toString());
    url = UtilsService.addOrUpdateUrlParam(url, "page", page.toString());

    return this.http.get<PaginatedApiResultInterface<PostInterface>>(url);
  }

  getContributorBadges(topicId: TopicInterface["id"]): Observable<Record<string, string[]>> {
    return this.http.get<Record<string, string[]>>(`${this.configUrl}/topic/${topicId}/badges/`);
  }

  getMultipleTopicBadges(topicIds: number[]): Observable<Record<string, { userId: string; badges: string[] }>> {
    const url = UtilsService.addOrUpdateUrlParam(`${this.configUrl}/topic/badges/`, "ids", topicIds.join(","));
    return this.http.get<Record<string, { userId: string; badges: string[] }>>(url);
  }

  getPostBadges(topicId: number): Observable<PostBadgesResponse> {
    return this.http.get<PostBadgesResponse>(`${this.baseUrl}/forum/topic/${topicId}/post-badges/`);
  }

  /**
   * Record a hit/view for a specific post
   */
  recordPostHit(postId: PostInterface["id"]): Observable<{ total_hits: number }> {
    return this.http.put<{ total_hits: number }>(`${this.configUrl}/post/${postId}/hit/`, {});
  }

  /**
   * Subscribe to a topic
   */
  subscribeTopic(topicId: TopicInterface["id"]): Observable<void> {
    return this.http.post<void>(`${this.configUrl}/topic/${topicId}/subscribe/`, {});
  }

  /**
   * Unsubscribe from a topic
   */
  unsubscribeTopic(topicId: TopicInterface["id"]): Observable<void> {
    return this.http.post<void>(`${this.configUrl}/topic/${topicId}/unsubscribe/`, {});
  }

  /**
   * Vote on a poll
   * @param topicId - ID of the topic with the poll
   * @param pollType - Type of the poll (single or multiple choice)
   * @param answerIds - ID or IDs of the selected answer(s)
   */
  votePoll(
    topicId: TopicInterface["id"],
    pollType: PollType,
    answerIds: number | number[]
  ): Observable<TopicInterface> {
    let payload: object;

    // Construct the payload based on poll type
    if (pollType === PollType.POLL_TYPE_SINGLE) {
      payload = { answerId: answerIds as number };
    } else {
      payload = { answerIds: answerIds as number[] };
    }

    // Make the API call
    return this.http.post<TopicInterface>(`${this.configUrl}/topic/${topicId}/vote/`, payload).pipe(
      map(topic => ({
        ...topic,
        primaryTag: topic.tags ? this.tagSelectionService.selectPrimaryTag(topic.tags) : null
      }))
    );
  }

  /**
   * Cancel a vote on a poll
   * @param topicId - ID of the topic with the poll
   */
  cancelPollVote(topicId: TopicInterface["id"]): Observable<TopicInterface> {
    return this.http.post<TopicInterface>(`${this.configUrl}/topic/${topicId}/cancel-vote/`, {}).pipe(
      map(topic => ({
        ...topic,
        primaryTag: topic.tags ? this.tagSelectionService.selectPrimaryTag(topic.tags) : null
      }))
    );
  }

  /**
   * Delete a forum post
   * @param postId - ID of the post to delete
   */
  deletePost(postId: PostInterface["id"]): Observable<void> {
    return this.http.delete<void>(`${this.configUrl}/post/${postId}/`);
  }

  /**
   * Mark a post as read, updating the user's read status in the topic
   * @param topicId - ID of the topic containing the post
   * @param postId - ID of the post to mark as read (all posts up to this one will be marked as read)
   * @returns Observable with updated topic data including read status
   */
  markPostAsRead(topicId: TopicInterface["id"], postId: PostInterface["id"]): Observable<TopicInterface> {
    return this.http.post<TopicInterface>(`${this.configUrl}/topic/${topicId}/mark-read/`, { post_id: postId }).pipe(
      map(topic => ({
        ...topic,
        primaryTag: topic.tags ? this.tagSelectionService.selectPrimaryTag(topic.tags) : null
      }))
    );
  }

  /**
   * Approve a post that is pending moderation
   * @param postId - ID of the post to approve
   * @returns Observable with the updated post data
   */
  approvePost(postId: PostInterface["id"]): Observable<PostInterface> {
    return this.http.post<PostInterface>(`${this.configUrl}/post/${postId}/approve/`, {});
  }

  /**
   * Get a specific tag by slug
   * @param tagSlug - The slug of the tag to retrieve
   */
  getTag(tagSlug: string): Observable<TagInterface> {
    let url = `${this.configUrl}/tag/`;
    url = UtilsService.addOrUpdateUrlParam(url, "slug", tagSlug);
    return this.http.get<TagInterface>(url);
  }

  /**
   * Get all tags with optional pagination and search
   * @param page - The page number to retrieve
   * @param search - Optional search term to filter tags
   * @param followed - Optional filter for followed tags only
   * @param ordering - Optional ordering parameter (e.g., "name", "-topic_count")
   * @param excludeCategories - Optional comma-separated categories to exclude
   * @param category - Optional specific category to include
   * @param pageSize - Optional number of results per page
   * @param user - Optional user ID to get followed tags for a specific user
   */
  getTags(
    page = 1,
    search?: string,
    followed?: boolean,
    ordering?: string,
    excludeCategories?: string,
    category?: string,
    pageSize?: number,
    user?: number
  ): Observable<PaginatedApiResultInterface<TagInterface>> {
    let url = `${this.configUrl}/tag/?page=${page}`;

    if (search) {
      url = UtilsService.addOrUpdateUrlParam(url, "search", search);
    }

    if (followed) {
      url = UtilsService.addOrUpdateUrlParam(url, "followed", "1");
    }

    if (ordering) {
      url = UtilsService.addOrUpdateUrlParam(url, "ordering", ordering);
    }

    if (excludeCategories) {
      url = UtilsService.addOrUpdateUrlParam(url, "exclude_categories", excludeCategories);
    }

    if (category) {
      url = UtilsService.addOrUpdateUrlParam(url, "category", category);
    }

    if (pageSize) {
      url = UtilsService.addOrUpdateUrlParam(url, "page_size", pageSize.toString());
    }

    if (user) {
      url = UtilsService.addOrUpdateUrlParam(url, "user", user.toString());
    }

    return this.http.get<PaginatedApiResultInterface<TagInterface>>(url);
  }

  /**
   * Get topics filtered by tag
   * @param tagSlug - The slug of the tag to filter by
   * @param page - The page number to retrieve
   * @param subscribed - Optional filter for subscribed topics only
   * @param name - Optional name filter
   * @param recentParticipants - Whether to include recent participants
   */
  getTopicsByTag(
    tagSlug: string,
    page = 1,
    subscribed?: boolean,
    name?: string,
    recentParticipants = true
  ): Observable<PaginatedApiResultInterface<TopicInterface>> {
    let url = `${this.configUrl}/topic/`;

    url = UtilsService.addOrUpdateUrlParam(url, "tag", tagSlug);
    url = UtilsService.addOrUpdateUrlParam(url, "page", page.toString());

    if (subscribed) {
      url = UtilsService.addOrUpdateUrlParam(url, "subscribed", "true");
    }

    if (name) {
      url = UtilsService.addOrUpdateUrlParam(url, "name", name);
    }

    if (recentParticipants) {
      url = UtilsService.addOrUpdateUrlParam(url, "recent_participants", "1");
    }

    return this.http.get<PaginatedApiResultInterface<TopicInterface>>(url).pipe(
      map(response => ({
        ...response,
        results: response.results.map(topic => ({
          ...topic,
          primaryTag: topic.tags ? this.tagSelectionService.selectPrimaryTag(topic.tags) : null
        }))
      }))
    );
  }

  /**
   * Get topics started by a specific user
   * @param username - The username of the user whose topics to retrieve
   * @param page - The page number to retrieve
   * @param name - Optional name filter
   * @param recentParticipants - Whether to include recent participants
   * @returns Observable with paginated list of topics started by the user
   */
  getTopicsByUser(
    username: string,
    page = 1,
    name?: string,
    recentParticipants = true,
    titleSearch?: string,
    dateFrom?: string,
    dateTo?: string
  ): Observable<PaginatedApiResultInterface<TopicInterface>> {
    let url = `${this.configUrl}/topic/`;
    url = UtilsService.addOrUpdateUrlParam(url, "username", username);
    url = UtilsService.addOrUpdateUrlParam(url, "page", page.toString());

    // Use helper method for search filter params
    url = this.addSearchFilterParams(url, {
      name,
      titleSearch,
      dateFrom,
      dateTo
    });

    if (recentParticipants) {
      url = UtilsService.addOrUpdateUrlParam(url, "recent_participants", "1");
    }

    return this.http.get<PaginatedApiResultInterface<TopicInterface>>(url).pipe(
      map(response => ({
        ...response,
        results: response.results.map(topic => ({
          ...topic,
          primaryTag: topic.tags ? this.tagSelectionService.selectPrimaryTag(topic.tags) : null
        }))
      }))
    );
  }

  /**
   * Generate a title suggestion for a forum topic based on the provided content
   * @param content - The forum post content to generate a title from
   * @param maxWords - Optional maximum word limit for the generated title
   * @returns Observable with the generated title suggestion
   */
  generateTopicTitle(
    content: string,
    maxWords: number = ForumConstants.DEFAULT_TITLE_MAX_WORDS
  ): Observable<TopicTitleGenerationResponseInterface> {
    const payload: TopicTitleGenerationRequestInterface = {
      content,
      maxWords
    };

    return this.http.post<TopicTitleGenerationResponseInterface>(`${this.configUrl}/title-generation/`, payload);
  }

  /**
   * Generate tag suggestions for a forum topic based on the provided content
   * @param content - The forum post content to generate tags from
   * @param maxTags - Optional maximum number of tags to generate
   * @returns Observable with the generated tag suggestions
   */
  generateTopicTags(
    content: string,
    maxTags: number = ForumConstants.DEFAULT_MAX_TAGS
  ): Observable<TopicTagGenerationResponseInterface> {
    const payload: TopicTagGenerationRequestInterface = {
      content,
      maxTags
    };

    return this.http.post<TopicTagGenerationResponseInterface>(`${this.configUrl}/tag-generation/`, payload);
  }

  /**
   * Update tags for an existing topic
   * @param topicId - ID of the topic to update
   * @param tagSlugs - Array of tag slugs to set (replaces all existing tags)
   * @returns Observable with the updated topic
   */
  updateTopicTags(topicId: TopicInterface["id"], tagSlugs: string[]): Observable<TopicInterface> {
    const payload = {
      tag_slugs: tagSlugs
    };

    return this.http.patch<TopicInterface>(`${this.configUrl}/topic/${topicId}/tags/`, payload).pipe(
      map(topic => ({
        ...topic,
        primaryTag: topic.tags ? this.tagSelectionService.selectPrimaryTag(topic.tags) : null
      }))
    );
  }

  /**
   * Create a new forum post (either a new topic or a reply)
   * @param data - The post data
   * @returns Observable with the created post
   */
  createPost(data: {
    body: string;
    bodyHtml?: string;
    forumId?: number;
    topicId?: number;
    topicName?: string;
    pollType?: PollType;
    pollQuestion?: string;
    pollAnswers?: string[];
    tagSlugs?: string[];
    attachmentIds?: number[];
    editReason?: string;
  }): Observable<PostInterface> {
    return this.http.post<PostInterface>(`${this.configUrl}/post/`, data);
  }

  /**
   * Get a properly formatted BBCode quote of a post
   * @param postId - ID of the post to quote
   * @returns Observable with the quote string
   */
  quotePost(postId: PostInterface["id"]): Observable<{ quote: string }> {
    return this.http.post<{ quote: string }>(`${this.configUrl}/post/${postId}/quote/`, { format: "html" });
  }

  /**
   * Update an existing forum post
   * @param postId - ID of the post to update
   * @param data - The updated post data
   * @returns Observable with the updated post
   */
  updatePost(
    postId: PostInterface["id"],
    data: { body: string; bodyHtml?: string; editReason?: string }
  ): Observable<PostInterface> {
    return this.http.patch<PostInterface>(`${this.configUrl}/post/${postId}/`, data);
  }

  /**
   * Search for similar topics based on title and content
   * @param data - The search request data
   * @returns Observable with paginated similar topics
   */
  searchSimilarTopics(
    data: SimilarTopicSearchRequestInterface
  ): Observable<PaginatedApiResultInterface<SimilarTopicInterface>> {
    return this.http.post<PaginatedApiResultInterface<SimilarTopicInterface>>(
      `${this.configUrl}/similar-topics/search/`,
      data
    );
  }

  /**
   * Get similarity explanation between provided text and a topic
   * @param text - The text to compare
   * @param topicId - ID of the topic to compare against
   * @returns Observable with explanation, topic_id, and topic_name
   */
  getSimilarityExplanation(
    text: string,
    topicId: number
  ): Observable<{ explanation: string; topic_id: number; topic_name: string }> {
    const url = UtilsService.addOrUpdateUrlParam(
      `${this.configUrl}/similarity-explanation/`,
      "text",
      encodeURIComponent(text)
    );
    const finalUrl = UtilsService.addOrUpdateUrlParam(url, "topic_id", topicId.toString());

    return this.http.get<{ explanation: string; topic_id: number; topic_name: string }>(finalUrl);
  }

  /**˜
   * Get tag description with equipment type support
   * @param tag - The tag to get description for
   * @returns The tag description string
   */
  getTagDescription(tag: TagInterface | TopicTagInterface): string {
    // Check if it's an equipment tag
    if (tag.equipmentType && tag.equipmentName) {
      const equipmentTypeLabel = this.translateService.instant("Equipment item");
      return `${equipmentTypeLabel}: ${tag.equipmentType}.`;
    }

    if (!tag.descriptions) {
      return "";
    }

    const currentLang = this.translateService.currentLang;

    // Try current language first
    if (tag.descriptions[currentLang]) {
      return tag.descriptions[currentLang];
    }

    // Fallback to English if available
    if (tag.descriptions["en"]) {
      return tag.descriptions["en"];
    }

    // Return empty string if no description available
    return "";
  }

  /**
   * Get personalized feed for authenticated user
   * @param page - Page number (default: 1)
   * @param searchTerm - Optional search term to filter topics
   * @param ordering - Sort order (default: -updated)
   * @param recentParticipants - Whether to include recent participants
   * @returns Observable with paginated topic list
   */
  getPersonalizedFeed(
    page = 1,
    searchTerm?: string,
    ordering = "-updated",
    recentParticipants = true,
    titleSearch?: string,
    dateFrom?: string,
    dateTo?: string,
    myTopics?: boolean,
    myPosts?: boolean
  ): Observable<PaginatedApiResultInterface<TopicInterface>> {
    let url = `${this.configUrl}/topic/personalized-feed/`;

    url = UtilsService.addOrUpdateUrlParam(url, "page", page.toString());
    url = UtilsService.addOrUpdateUrlParam(url, "ordering", ordering);

    // Use helper method for search filter params
    url = this.addSearchFilterParams(url, {
      name: searchTerm,
      titleSearch,
      dateFrom,
      dateTo,
      myTopics,
      myPosts
    });

    if (recentParticipants) {
      url = UtilsService.addOrUpdateUrlParam(url, "recent_participants", "1");
    }

    return this.http.get<PaginatedApiResultInterface<TopicInterface>>(url).pipe(
      map(response => ({
        ...response,
        results: response.results.map(topic => ({
          ...topic,
          primaryTag: topic.tags ? this.tagSelectionService.selectPrimaryTag(topic.tags) : null
        }))
      }))
    );
  }

  /**
   * Mark a tag as read, clearing its unread count
   * @param tagId - ID of the tag to mark as read
   * @returns Observable with updated tag data
   */
  markTagAsRead(tagId: TagInterface["id"]): Observable<TagInterface> {
    return this.http.post<TagInterface>(`${this.configUrl}/tag/${tagId}/mark-read/`, {});
  }

  /**
   * Get posts by a specific user
   * @param userId - The ID of the user whose posts to retrieve
   * @param page - The page number to retrieve
   * @param bodyText - Optional text filter for post content
   * @returns Observable with paginated list of posts by the user
   */
  getPostsByUser(userId: number, page = 1, bodyText?: string): Observable<PaginatedApiResultInterface<PostInterface>> {
    let url = `${this.configUrl}/post/`;

    url = UtilsService.addOrUpdateUrlParam(url, "user", userId.toString());
    url = UtilsService.addOrUpdateUrlParam(url, "page", page.toString());
    url = UtilsService.addOrUpdateUrlParam(url, "ordering", "-created");

    if (bodyText) {
      url = UtilsService.addOrUpdateUrlParam(url, "body_text", bodyText);
    }

    return this.http.get<PaginatedApiResultInterface<PostInterface>>(url);
  }

  // Community Notes methods
  getCommunityNotes(postId: number): Observable<PaginatedApiResultInterface<CommunityNoteInterface>> {
    interface ApiNoteResponse {
      id: number;
      post: number;
      author: any;
      note_type: string;
      body: string;
      sources: string[];
      created_at: string;
      updated_at: string;
      vote_count: number;
      user_has_voted: boolean;
      is_author_expert: boolean;
      can_edit: boolean;
      edit_count: number;
    }

    interface ApiPaginatedResponse {
      count: number;
      next: string | null;
      previous: string | null;
      results: ApiNoteResponse[];
    }

    return this.http.get<ApiPaginatedResponse>(`${this.configUrl}/community-notes/by_post/?post_id=${postId}`).pipe(
      map(response => ({
        count: response.count,
        next: response.next,
        prev: response.previous, // Map 'previous' to 'prev'
        results: response.results.map(note => UtilsService.objectToCamelCase(note) as CommunityNoteInterface)
      }))
    );
  }

  createCommunityNote(postId: number, data: CreateCommunityNoteDto): Observable<CommunityNoteInterface> {
    const snakeCaseData = UtilsService.objectToSnakeCase(data);

    interface ApiResponse {
      id: number;
      post: number;
      author: any;
      note_type: string;
      body: string;
      sources: string[];
      created_at: string;
      updated_at: string;
      vote_count: number;
      user_has_voted: boolean;
      is_author_expert: boolean;
      can_edit: boolean;
      edit_count: number;
    }

    return this.http
      .post<ApiResponse>(`${this.configUrl}/community-notes/`, snakeCaseData)
      .pipe(map(response => UtilsService.objectToCamelCase(response) as CommunityNoteInterface));
  }

  updateCommunityNote(noteId: number, data: UpdateCommunityNoteDto): Observable<CommunityNoteInterface> {
    const snakeCaseData = UtilsService.objectToSnakeCase(data);

    interface ApiResponse {
      id: number;
      post: number;
      author: any;
      note_type: string;
      body: string;
      sources: string[];
      created_at: string;
      updated_at: string;
      vote_count: number;
      user_has_voted: boolean;
      is_author_expert: boolean;
      can_edit: boolean;
      edit_count: number;
    }

    return this.http
      .patch<ApiResponse>(`${this.configUrl}/community-notes/${noteId}/`, snakeCaseData)
      .pipe(map(response => UtilsService.objectToCamelCase(response) as CommunityNoteInterface));
  }

  deleteCommunityNote(noteId: number): Observable<void> {
    return this.http.delete<void>(`${this.configUrl}/community-notes/${noteId}/`);
  }

  upvoteCommunityNote(noteId: number): Observable<CommunityNoteVoteInterface> {
    interface ApiResponse {
      id: number;
      note: number;
      user: number;
      created_at: string;
    }

    return this.http
      .post<ApiResponse>(`${this.configUrl}/community-notes/${noteId}/upvote/`, {})
      .pipe(map(response => UtilsService.objectToCamelCase(response) as CommunityNoteVoteInterface));
  }

  removeUpvoteCommunityNote(noteId: number): Observable<void> {
    return this.http.delete<void>(`${this.configUrl}/community-notes/${noteId}/upvote/`);
  }

  getCommunityNoteHistory(noteId: number): Observable<CommunityNoteHistoryInterface> {
    interface ApiResponse {
      note_id: number;
      created_at: string;
      current_body: string;
      current_sources: string[];
      author: any;
      edits: Array<{
        id: number;
        note: number;
        editor: any;
        previous_body: string;
        new_body: string;
        previous_sources: string[];
        new_sources: string[];
        timestamp: string;
        body_diff: string[];
        sources_diff: {
          added: string[];
          removed: string[];
        };
        changes_summary: string[];
      }>;
      total_edits: number;
    }

    return this.http
      .get<ApiResponse>(`${this.configUrl}/community-notes/${noteId}/history/`)
      .pipe(map(response => UtilsService.objectToCamelCase(response) as CommunityNoteHistoryInterface));
  }

  /**
   * Get personalized tag recommendations for the current user
   * @returns Observable with tag recommendations in three categories: popular, equipment, and celestialObjects
   */
  getTagRecommendations(): Observable<TagRecommendationsInterface> {
    return this.http
      .get<TagRecommendationsInterface>(`${this.configUrl}/tag-recommendation/`)
      .pipe(map(response => UtilsService.objectToCamelCase(response) as TagRecommendationsInterface));
  }

  /**
   * Accept suggested tags for a topic
   * @param topicId - ID of the topic
   * @param tagSlugs - Array of tag slugs to accept
   * @returns Observable with void
   */
  acceptTopicTags(topicId: TopicInterface["id"], tagSlugs: string[]): Observable<void> {
    const payload: TagReviewRequestInterface = {
      tag_slugs: tagSlugs
    };

    return this.http.post<void>(`${this.configUrl}/topic/${topicId}/accept-tags/`, payload);
  }

  /**
   * Reject suggested tags for a topic
   * @param topicId - ID of the topic
   * @param tagSlugs - Array of tag slugs to reject
   * @returns Observable with void
   */
  rejectTopicTags(topicId: TopicInterface["id"], tagSlugs: string[]): Observable<void> {
    const payload: TagReviewRequestInterface = {
      tag_slugs: tagSlugs
    };

    return this.http.post<void>(`${this.configUrl}/topic/${topicId}/reject-tags/`, payload);
  }

  /**
   * Close a topic, preventing new replies
   * @param topicId - ID of the topic to close
   * @returns Observable with response message
   */
  closeTopic(topicId: TopicInterface["id"]): Observable<{ detail: string }> {
    return this.http.post<{ detail: string }>(`${this.configUrl}/topic/${topicId}/close/`, {});
  }

  /**
   * Reopen a previously closed topic
   * @param topicId - ID of the topic to reopen
   * @returns Observable with response message
   */
  reopenTopic(topicId: TopicInterface["id"]): Observable<{ detail: string }> {
    return this.http.post<{ detail: string }>(`${this.configUrl}/topic/${topicId}/reopen/`, {});
  }

  /**
   * Make a topic sticky (pin to top)
   * @param topicId - ID of the topic to make sticky
   * @returns Observable with response message
   */
  makeStickyTopic(topicId: TopicInterface["id"]): Observable<{ detail: string }> {
    return this.http.post<{ detail: string }>(`${this.configUrl}/topic/${topicId}/sticky/`, {});
  }

  /**
   * Remove sticky status from a topic
   * @param topicId - ID of the topic to unsticky
   * @returns Observable with response message
   */
  removeStickyTopic(topicId: TopicInterface["id"]): Observable<{ detail: string }> {
    return this.http.post<{ detail: string }>(`${this.configUrl}/topic/${topicId}/unsticky/`, {});
  }

  /**
   * Move a topic to a different forum
   * @param topicId - ID of the topic to move
   * @param forumId - ID of the target forum
   * @returns Observable with move result details
   */
  moveTopic(
    topicId: TopicInterface["id"],
    forumId: ForumInterface["id"]
  ): Observable<{
    detail: string;
    new_forum_id: number;
    new_forum_name: string;
    new_slug: string;
  }> {
    return this.http.post<{
      detail: string;
      new_forum_id: number;
      new_forum_name: string;
      new_slug: string;
    }>(`${this.configUrl}/topic/${topicId}/move/`, { forum_id: forumId });
  }

  /**
   * Search forums by name
   * @param query - Search query
   * @param page - Page number (default: 1)
   * @param pageSize - Number of results per page (default: 20)
   * @returns Observable with paginated forum results
   */
  searchForums(query: string, page = 1, pageSize = 20): Observable<PaginatedApiResultInterface<ForumInterface>> {
    let url = `${this.configUrl}/forum/?page=${page}&page_size=${pageSize}`;

    if (query) {
      url = UtilsService.addOrUpdateUrlParam(url, "q", query);
    }

    return this.http.get<PaginatedApiResultInterface<ForumInterface>>(url);
  }
}
